import { useEffect, useMemo, useRef, useState } from 'react'

import { BarChartOutlined, LineChartOutlined } from '@ant-design/icons'
import { useBoolean, useRequest } from 'ahooks'
import { Button, DatePicker, Flex, Select, Tooltip } from 'antd'
import { createStyles } from 'antd-style'
import dayjs, { Dayjs } from 'dayjs'

import { queryPriceIndexListGroupKindCodeApi } from '@eastcoal/api/content/priceIndexMall/queryPriceIndexListGroupKindCode'

import { Chart } from '@eastcoal/common/components/Chart'
import { formatDate } from '@eastcoal/common/utils/date'
import { excludeNilFilter } from '@eastcoal/common/utils/lambda'

import { PriceIndexType } from '@eastcoal/shared/enums/PriceIndexType'

import { PriceIndexChartProps } from './interface'

const useStyles = createStyles(({ token }) => ({
  container: {
    position: 'relative'
  },
  switchButton: {
    position: 'absolute',
    top: 0,
    right: token.marginXS
  }
}))

export const PriceIndexChart = ({ kindCodes }: PriceIndexChartProps) => {
  const [timeRange, setTimeRange] = useState<[Dayjs | null, Dayjs | null]>([
    dayjs().subtract(1, 'month'),
    dayjs()
  ])
  const { data } = useRequest(
    () =>
      queryPriceIndexListGroupKindCodeApi(
        {
          kindCodes,
          priceIndexType: PriceIndexType.PRICE_INDEX,
          startDate: formatDate(timeRange[0]),
          endDate: formatDate(timeRange[1])
        },
        { silence: true }
      ),
    {
      manual: false,
      refreshDeps: [timeRange]
    }
  )

  const [dataIndex, setDataIndex] = useState(0)
  const selectOptions = useMemo(() => {
    if (!data?.length) return []
    return data.map((item, index) => ({
      label: item.kindName,
      value: index
    }))
  }, [data])
  const selectUpdateTimerRef = useRef<number>(null)
  const handleSelectChange = (value: number) => {
    setDataIndex(value)
    if (selectUpdateTimerRef.current) {
      window.clearTimeout(selectUpdateTimerRef.current)
    }
  }
  useEffect(() => {
    const updateSelectValue = () => {
      selectUpdateTimerRef.current = window.setTimeout(() => {
        setDataIndex(prev => {
          if (prev >= selectOptions.length - 1) {
            return 0
          }
          return prev + 1
        })
        updateSelectValue()
      }, 5000)
    }
    updateSelectValue()
    return () => {
      if (selectUpdateTimerRef.current) {
        window.clearTimeout(selectUpdateTimerRef.current)
      }
    }
  }, [selectOptions.length])

  const chartData = useMemo(() => {
    if (!data?.length) return { labels: [], datasets: [] }
    const selectedData = data[dataIndex]
    const selectedPriceIndexList = (selectedData.priceIndexList || []).filter(
      item => !!item.indexTime
    )
    return {
      labels: selectedPriceIndexList.map(item => item.indexTime),
      datasets: [
        {
          label: selectedData.kindName,
          data: selectedPriceIndexList
            .map(item =>
              item.indexTime
                ? {
                    x: item.indexTime,
                    y: item.price ?? 0
                  }
                : undefined
            )
            .filter(excludeNilFilter)
        }
      ]
    }
  }, [data, dataIndex])

  const [isLineChart, isLineChartActions] = useBoolean(true)

  const { styles } = useStyles()

  return (
    <div className={styles.container}>
      <Flex align='center' gap={12} justify='end'>
        <Select
          options={selectOptions}
          size='small'
          style={{ width: 120 }}
          value={dataIndex}
          onChange={handleSelectChange}
        />
        <DatePicker.RangePicker
          showNow
          format='YYYY-MM-DD'
          size='small'
          value={timeRange}
          onChange={values => {
            if (values) {
              setTimeRange(values)
            } else {
              setTimeRange([] as any)
            }
          }}
        />
        <Tooltip title={isLineChart ? '柱状图' : '折线图'}>
          <Button
            icon={isLineChart ? <BarChartOutlined /> : <LineChartOutlined />}
            size='small'
            onClick={isLineChartActions.toggle}
          />
        </Tooltip>
      </Flex>
      <Chart
        data={chartData as any}
        refreshDeps={[isLineChart, chartData]}
        type={isLineChart ? 'line' : 'bar'}
        options={{
          responsive: true,
          interaction: {
            intersect: false,
            axis: 'x'
          },
          plugins: {
            subtitle: {
              display: true,
              text: '元/吨',
              position: 'top',
              align: 'start',
              padding: {
                top: 0,
                bottom: 12
              }
            },
            legend: {
              display: false,
              position: 'bottom'
            }
          }
        }}
      />
    </div>
  )
}
